import { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../index';

interface MediaItem {
  id: number;
  date_added: string;
  name: string;
  r2_filename: string;
  public_url: string;
}

interface MediaResponse {
  media: MediaItem[];
  totalMedia: number;
}

export const useMedia = (showAll: boolean = false, page: number = 1, limit: number = 10) => {
  const [data, setData] = useState<MediaItem[]>([]);
  const [totalMedia, setTotalMedia] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchMedia = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        ...(showAll && { show_all: 'true' })
      });
      const result = await apiClient.get<MediaResponse>(`/api/media?${params}`);
      setData(result.media);
      setTotalMedia(result.totalMedia);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch media');
    } finally {
      setLoading(false);
    }
  }, [showAll, page, limit]);

  const uploadMedia = useCallback(async (formData: FormData) => {
    const result = await apiClient.upload<MediaItem>('/api/media', formData);
    await fetchMedia();
    return result;
  }, [fetchMedia]);

  const deleteMedia = useCallback(async (id: number) => {
    await apiClient.delete(`/api/media/${id}`);
    await fetchMedia();
  }, [fetchMedia]);

  useEffect(() => {
    fetchMedia();
  }, [fetchMedia]);

  return {
    data,
    totalMedia,
    loading,
    error,
    refetch: fetchMedia,
    uploadMedia,
    deleteMedia,
  };
};